import { t } from '@lingui/core/macro';
import { Microphone01 } from '@untitledui/icons/Microphone01';
import { Type01 } from '@untitledui/icons/Type01';
import { VideoRecorder } from '@untitledui/icons/VideoRecorder';

import type {
  InterviewDeliveryMode,
  StudyLanguage,
} from '@/api/core-api/types.gen';

import { Select } from '@/components/base/select/select';
import { DEFAULT_STUDY_LANGUAGE as SHARED_DEFAULT_STUDY_LANGUAGE } from '@common/utils';

import { ResponseLanguagesSelect } from './response-languages-select';
import { StudyLanguageSelect } from './study-language-select';

/** Default interview medium, matching the editor's AI-mod default. */
export const DEFAULT_INTERVIEW_MEDIUM: InterviewDeliveryMode = 'video';

// Re-exported so existing importers keep this specifier.
export const DEFAULT_STUDY_LANGUAGE = SHARED_DEFAULT_STUDY_LANGUAGE;

export const DELIVERY_MODE_ICONS: Record<
  InterviewDeliveryMode,
  typeof VideoRecorder
> = {
  video: VideoRecorder,
  audio: Microphone01,
  text: Type01,
};

function getDeliveryModeOptions(): Array<{
  id: InterviewDeliveryMode;
  label: string;
}> {
  return [
    { id: 'video', label: t`Video` },
    { id: 'audio', label: t`Audio` },
    { id: 'text', label: t`Text` },
  ];
}

interface AiInterviewSettingsProps {
  medium: InterviewDeliveryMode;
  onMediumChange: (medium: InterviewDeliveryMode) => void;
  language: StudyLanguage;
  onLanguageChange: (language: StudyLanguage) => void;
  responseLanguages: StudyLanguage[];
  onResponseLanguagesChange: (languages: StudyLanguage[]) => void;
}

/**
 * AI in-house path settings. Selecting the AI platform fixes the study type to
 * AI_MODERATED_INTERVIEW and captures the interview medium and language, both
 * persisted separately once creation starts.
 */
export function AiInterviewSettings({
  medium,
  onMediumChange,
  language,
  onLanguageChange,
  responseLanguages,
  onResponseLanguagesChange,
}: AiInterviewSettingsProps) {
  return (
    <div className="grid gap-4">
      <Select<InterviewDeliveryMode>
        label={t`Interview format`}
        value={medium}
        onSelectionChange={onMediumChange}
        items={getDeliveryModeOptions()}
        placeholder={t`Select a format`}
      >
        {(item) => (
          <Select.Item {...item} icon={DELIVERY_MODE_ICONS[item.id]} />
        )}
      </Select>
      <StudyLanguageSelect value={language} onChange={onLanguageChange} />
      <ResponseLanguagesSelect
        studyLanguage={language}
        value={responseLanguages}
        onChange={onResponseLanguagesChange}
      />
    </div>
  );
}
